import { LogoLoadingSpinner } from "~/components/LoadingSpinner";
import { LeagueTableTab } from "~/modules/league_tables/types";
import { LeagueTable } from "~/modules/league_tables/components/LeagueTable";
import { LeagueTableChartConfig } from "~/modules/charts/types";
import { api } from "~/utils/api";
import { deobfuscate } from "~/utils/obfuscate";
import {
    getLeagueTableGroups,
    getLeagueTableMaxRows,
    getLeagueTableMeasureSlug,
} from "../ChartForm/league_table/leagueTableConfig";

// // // //

// Prefixes the `name` of each token aggregates query the API issues per tab
const LEAGUE_TABLE_QUERY_NAME = "chart-league-table";

const selectLeagueTableTabs = (data: unknown) => {
    return deobfuscate(data as string) as LeagueTableTab[];
};

/**
 * LeagueTableChartProvider
 * Fetches the league table tabs (one per configured group) from the token
 * aggregates API and hands the loading, empty, and chart elements to its
 * children to lay out.
 */
export function LeagueTableChartProvider(props: {
    title: string;
    config: LeagueTableChartConfig;
    children: (props: {
        isLoading: boolean;
        isEmpty: boolean;
        chartEl: React.ReactNode;
        emptyEl: React.ReactNode;
        loadingEl: React.ReactNode;
    }) => React.ReactNode;
}) {
    const { title, config } = props;

    const groups = getLeagueTableGroups(config);
    const maxRows = getLeagueTableMaxRows(config);
    const measureSlug = getLeagueTableMeasureSlug(config);

    const queryReq = api.leagueTables.queryLeagueTable.useQuery(
        {
            name: LEAGUE_TABLE_QUERY_NAME,
            query: config.query,
            // Sorted copy so reordering tabs reuses the cached query instead of
            // refetching (and without mutating config.groups in place)
            groups: [...groups].sort(),
            measureSlug,
        },
        {
            // Nothing to rank until at least one group tab is configured
            enabled: groups.length > 0,
            refetchOnWindowFocus: false,
            staleTime: Infinity,
            keepPreviousData: true,
            select: selectLeagueTableTabs,
        },
    );

    const hasGroups = groups.length > 0;

    // NOTE - a disabled query still reports isLoading, so gate on hasGroups
    const isLoading = hasGroups && queryReq.isLoading;

    // The API returns one tab per requested group, in the (sorted) requested order
    const tabs = queryReq.data ?? [];
    const hasRows = tabs.some((tab) => tab.data.rows.length > 0);

    const isEmpty = !isLoading && (!hasGroups || !!queryReq.error || !hasRows);

    // Restore the config.groups display order
    // Copy first so the cached query data isn't mutated
    // NOTE - we do this because the API returns the tabs in the (sorted) requested order,
    // which is a result of sorting the config.groups value when we pass it to the API above
    const sortedTabs = [...tabs].sort((a, b) => {
        const aIndex = groups.indexOf(a.data.groupType);
        const bIndex = groups.indexOf(b.data.groupType);
        return aIndex - bIndex;
    });

    const loadingEl = (
        <div className="flex h-[400px] items-center justify-center">
            <LogoLoadingSpinner size={75} />
        </div>
    );

    // Explains why there's nothing to render: no groups, a failed query, or no rows
    const emptyEl = !hasGroups ? (
        <div className="flex h-[400px] items-center justify-center text-sm text-muted-foreground">
            Select at least one group to render the league table.
        </div>
    ) : queryReq.error ? (
        <div className="flex h-[400px] items-center justify-center text-sm text-destructive">
            Failed to load the league table.
        </div>
    ) : (
        <div className="flex h-[400px] items-center justify-center text-sm text-muted-foreground">
            No data matches the current filters.
        </div>
    );

    const chartEl = (
        <LeagueTable
            cardTitle={title}
            tabs={sortedTabs}
            loading={queryReq.isFetching}
            analyticsTableName="chart-league-table-preview"
            filterEmptyTabs={false}
            maxRows={maxRows}
            colSpans={{
                sm: 12,
                md: 12,
                lg: 12,
                xl: 12,
                xxl: 12,
            }}
        />
    );

    return (
        <>
            {props.children({
                isLoading,
                isEmpty,
                chartEl,
                emptyEl,
                loadingEl,
            })}
        </>
    );
}
